import { Component, type ReactNode } from "react"
import { Button } from "@/components/ui/button"

interface Props {
  children: ReactNode
  fallbackTitle?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  handleGoHome = () => {
    this.setState({ hasError: false, error: null })
    window.location.href = "/"
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[320px] p-6 text-center">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 max-w-md w-full shadow-sm">
            <h2 className="text-lg font-semibold text-amber-900 mb-2">
              {this.props.fallbackTitle ?? "Something went wrong"}
            </h2>
            <p className="text-sm text-amber-700 mb-4">
              This section failed to load. Your data is safe and the rest of the app is still usable.
            </p>
            {this.state.error ? (
              <p className="text-xs text-amber-600/70 mb-4 font-mono break-all">
                {this.state.error.message}
              </p>
            ) : null}
            <div className="flex gap-2 justify-center">
              <Button variant="outline" size="sm" onClick={this.handleRetry}>
                Retry
              </Button>
              <Button variant="default" size="sm" onClick={this.handleGoHome}>
                Go Home
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
